import { ReactElement, useEffect, useRef, useState } from "react";
import { cloneDeep } from "lodash";
import { Box } from "@twilio-paste/core/box";
import { styled } from "@twilio-paste/core/styling-library";
import { Theme } from "@twilio-paste/core/theme";

import {
  MessageBlock,
  WiseOwlBlock,
  WiseOwlGetChatSummaryByIdResponseData,
  WiseOwlSyncDocumentContent,
  trackingDetails,
  NewAiMessageStatus,
  SendMessageFormValues,
  CustomerFeedbackFormValues,
  WiseOwlLoadingConfiguration,
  WiseOwlProps,
  WiseOwlEventEnum,
  MessageCompletionStatus,
  WiseOwlBlockType,
  MessageBlockRole,
  WiseOwlElementIds,
  WiseOwlIngress,
  WiseOwlApiResponse,
  WiseOwlGetChatAboutTicketIdResponseData,
  ZendeskSidebarTicket,
  SurveyVariantEnum,
  LiveChatPreEngagementValues,
  LiveChatBlock,
  HandleLiveChatTransferArgs,
  WiseOwlTransferToLiveChatResponseData,
  HandleDirectChatCreationArgs,
  ZendeskTags,
  wiseOwlErrors,
  WiseOwlContextProps,
  getOscConfig,
  DisplayPromptLibrary,
} from "@twiliointernal/e2-commons-shared-definitions";
import {
  useScript,
  useTwilioSyncDocument,
} from "@twiliointernal/e2-commons-shared-hooks";
import { buildLiveChatPreEngagementData } from "@twiliointernal/e2-commons-live-chat";
import { SurveyProvider } from "@twiliointernal/e2-commons-csat-survey";

import { constants } from "./definitions";
import WiseOwlChat from "../WiseOwlChat";
import {
  useCreateChat,
  useSendMessage,
  useSubmitFeedback,
  useGetChatSummary,
  useGetChat,
  WiseOwlProvider,
  useSessionStorage,
  useTransferToLiveChat,
} from "../../hooks";
import {
  createNewUserMessageBlock,
  findLastFailedUserMessage,
  findUserPromptMessageToRegenerateAssistantMessage,
  getLiveChatValuesFromBlocks,
  hasMessages,
  isLiveChat,
  setUserMessageBlockFailed,
  spliceBlocks,
} from "../../components/blockRenderer/helpers";
import { SESSION_KEY } from "../../components/blockRenderer/definitions/constants";
import { LegalDisclaimer } from "./sub/legalDisclaimer";
import styles from "./wiseOwl.module.css";

const { recaptchaSiteKey } = getOscConfig();

const WiseOwlStyleContainer = styled(Box)<{ customStyles?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  ${({ customStyles }) => (customStyles ? customStyles : "")}
`;

const WiseOwl = (props: WiseOwlProps): ReactElement => {
  const {
    displayName,
    ingress,
    chatId: chatIdFromProps,
    customStyles,
    liveChatConfiguration,
    onEvent,
    onError,
    onTrackEvent,
    onContextInit,
    stargateUngatedUserId,
    surveyId,
    ticket,
    user,
    externalUserMessage,
    ingressLiveChatPreEngagementData: ingressPreEngagementData,
  } = props;

  const [loadingConfiguration, updateLoadingConfiguration] =
    useState<WiseOwlLoadingConfiguration>(constants.defaultLoadingState);

  const chatId = useRef<string | undefined>(chatIdFromProps);
  const [interruptedChatId, setInterruptedChatId] = useSessionStorage<
    string | undefined
  >(SESSION_KEY.INTERRUPTED_CHAT_ID);

  const [newAiMessageStatus, setNewAiMessageStatus] =
    useState<NewAiMessageStatus>(NewAiMessageStatus.None);
  const [blocks, setBlocks] = useState<WiseOwlBlock[]>([]);
  const previousBlocks = useRef<WiseOwlBlock[]>([]);
  const [failedUserMessageId, setFailedUserMessageId] = useState<string>();
  const [disableChatMessageSubmission, setChatSubmissionDisabled] =
    useState<boolean>();
  const [isLiveChatPreEngagementOpen, setIsLiveChatPreEngagementOpen] =
    useState<boolean>(false);
  const [liveChatPreEngagementValues, setLiveChatPreEngagementValues] =
    useState<LiveChatPreEngagementValues>({
      productCategory: "",
      message: "",
    });

  const { mutateAsync: createChatMutation } = useCreateChat(props);
  const { mutateAsync: sendMessageMutation } = useSendMessage(props);
  const { mutateAsync: createFeedbackMutation } = useSubmitFeedback(props);
  const { mutateAsync: getChatMutation } = useGetChat(props);
  const { mutateAsync: getChatSummaryMutation } = useGetChatSummary(props);
  const { mutateAsync: transferToLiveChatMutation } =
    useTransferToLiveChat(props);

  /**
   * Only attach recaptcha if:
   * - Not in an internal application (Salesforce, Zendesk)
   * - Recaptcha is not already attached in the application
   */
  const shouldAttachRecaptcha =
    ingress !== WiseOwlIngress.salesforce && ingress !== WiseOwlIngress.zendesk;
  const isRecaptchaAttached = !!document.querySelector(
    'script[src*="https://www.google.com/recaptcha/api"]'
  );
  useScript({
    disabled: shouldAttachRecaptcha ? isRecaptchaAttached : true,
    src: WiseOwlIngress.console
      ? `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}&badge=invisible`
      : `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}&badge=bottomleft`,
  });

  useEffect(() => {
    /**
     * Fetch a chat on load if a chatId is present or by aboutTicketId
     * if `ticket` is present.
     */
    const fetchChatOnMount = async (): Promise<void> => {
      const currentChatId = chatIdFromProps || interruptedChatId;
      if (currentChatId) {
        chatId.current = currentChatId;

        await handleGetChatById(chatId.current, interruptedChatId);
      }
    };

    if (ticket) {
      handleGetChatByAboutTicketId(ticket);
    } else {
      fetchChatOnMount();
    }
  }, []);

  useEffect(() => {
    if (externalUserMessage) {
      if (chatId.current) {
        handleSendChatMessage({
          chatId: chatId.current,
          input: externalUserMessage,
        });
      } else {
        handleStartNewChat({
          input: externalUserMessage,
        });
      }
    }
  }, [externalUserMessage]);

  useEffect(() => {
    /**
     * Incoming chatIdFromProps, update state and fetch
     * - Do not fetch if submitting a new chat
     */
    if (
      !chatId.current &&
      chatIdFromProps &&
      !loadingConfiguration.submission
    ) {
      chatId.current = chatIdFromProps;
      handleGetChatById(chatIdFromProps);
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: true,
      });
    }

    /**
     * chatIdFromProps removed, reset state
     * - Do not fetch if submitting a new chat
     */
    if (
      chatId.current &&
      !chatIdFromProps &&
      !loadingConfiguration.submission
    ) {
      chatId.current = undefined;
      setBlocks([]);
    }
  }, [chatIdFromProps]);

  // Locates any failed user Message, updates "success" value to false, and inserts newly updated block into state.
  useEffect(() => {
    if (blocks.length && failedUserMessageId) {
      // Identify index of failed message
      const { failedMessageIndex, failedMessage } = findLastFailedUserMessage(
        blocks,
        failedUserMessageId
      );

      // Set success value to false in cloned block
      const updatedBlock = setUserMessageBlockFailed(failedMessage);

      // Replaces original failed message block with updated block.
      setBlocks((currentBlocks) =>
        spliceBlocks(currentBlocks, failedMessageIndex, updatedBlock)
      );
    }
  }, [failedUserMessageId]);

  const handleGetChatByAboutTicketId = async (
    ticketToFetch: ZendeskSidebarTicket
  ) => {
    const handleGetChatByAboutTicketIdError = (err: Error) => {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.getChatByAboutTicketId,
          data: { aboutTicketId: ticketToFetch.id },
          error: err,
        });
      }

      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: false,
      });

      chatId.current = undefined;
    };

    try {
      /**
       * Non-null assertion used to prevent TS errors, see notes below
       *
       * Some TypeScript trickiness here with the useGetChat hook, open to suggestions:
       *
       * - The hook requires a `chatId`, but we don't use it for Zendesk
       * - Various TS arg implementations have been tried, but making the `chatId`
       *   optional causes more errors for OSC, Console, etc.
       * - Zendesk only cares if the `ticket` is present which is required when the
       *   ingress is `zendesk`, so the setting of `chatId` here is superficial
       */
      const response = await getChatMutation(
        { chatId: chatId.current! },
        {
          /**
           * It's better to handle the mutation error with the onError callback for getChatMutation.
           * This allows for better error log specificity vs just throwing/catching a generic error .
           */
          onError: handleGetChatByAboutTicketIdError,
        }
      );
      const {
        data: { chats, blocks },
      } =
        response as WiseOwlApiResponse<WiseOwlGetChatAboutTicketIdResponseData>;

      // There are no chats created for the ticket
      if (!chats?.length) {
        chatId.current = undefined;
      }

      if (chats?.length) {
        chatId.current = chats[0].id;
        setBlocks(blocks);
      }

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.getChatByAboutTicketId,
          data: {
            aboutTicketId: ticketToFetch.id,
          },
        });
      }
    } catch (err) {
      handleGetChatByAboutTicketIdError(err as Error);
    }
  };

  const handleGetChatById = async (
    chatIdToFetch: string,
    interruptedChatId?: string
  ): Promise<void> => {
    try {
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: true,
      });

      const response = await getChatMutation({
        chatId: chatIdToFetch,
        interruptedChatId,
      });
      const isInterruptedChat =
        !!interruptedChatId && chatIdToFetch === interruptedChatId;

      const { blocks } = response.data;

      const liveChatBlock = blocks.find(
        (block) => block.type === WiseOwlBlockType.LiveChat
      ) as LiveChatBlock;

      setBlocks(blocks);

      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: false,
      });

      if (isInterruptedChat) {
        /**
         * Find the last message sent by the User
         * and resend it.
         */
        const lastUserMessage = blocks
          .slice()
          .reverse()
          .find(
            (block) =>
              block.type === WiseOwlBlockType.Message &&
              block.values.role === MessageBlockRole.User
          ) as MessageBlock;

        await handleSendChatMessage({
          chatId: chatIdToFetch,
          input: lastUserMessage?.values.message,
        });
      }

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.getChat,
          data: {
            chatId: chatIdToFetch,
            isLiveChatActive: !!liveChatBlock,
            liveChatId: liveChatBlock?.values.liveChatId,
            /**
             * If the chat was interrupted, we set resumeChat to true
             * so that the consuming application can handle the navigation
             * to the chat page.
             */
            resumeChat: isInterruptedChat,
          },
        });
      }
      setInterruptedChatId(undefined);
      chatId.current = chatIdToFetch;
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.getChat,
          data: { chatId: chatIdToFetch },
          error: err as Error,
        });
      }

      /**
       * TODO: Proper messaging/handling on chatId fetch error
       * It's likely an error occurs here because the chatId is not found
       * or doesn't belong to the current user. In this case, we reset the
       * chatId and navigate to the home page under the hood
       */
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: false,
      });
      chatId.current = undefined;
    }
  };

  const handleChatButtonToolClick = (payload: string): void => {
    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.chatButtonToolClick,
        data: {
          chatToolType: payload,
        },
      });
    }
  };

  const handleDisplayPrompt = ({
    chatToolType,
    displayPrompt,
  }: DisplayPromptLibrary): void => {
    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.displayPromptLibrary,
        data: {
          chatToolType,
          displayPrompt,
        },
      });
    }
  };

  const handleOnInitializeStream = (
    documentData: WiseOwlSyncDocumentContent
  ): void => {
    const { blocks: newBlocks } = documentData;

    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.streamInitialize,
        data: {
          chatId: chatId.current,
        },
      });
    }

    setNewAiMessageStatus(NewAiMessageStatus.Loading);

    /**
     * The document will have some initial content to render on sync
     * instantiation so we want to render that before the stream starts
     */

    setBlocks((currentBlocks) => currentBlocks.concat(newBlocks));
  };

  const handleOnStream = (documentData: WiseOwlSyncDocumentContent): void => {
    const { blocks: newBlocks } = documentData;

    setBlocks(previousBlocks.current.concat(newBlocks));
  };

  const handleOnStreamComplete = (
    documentData: WiseOwlSyncDocumentContent
  ): void => {
    const { blocks: newBlocks } = documentData;

    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.streamComplete,
        data: {
          chatId: chatId.current,
        },
      });
    }

    /**
     * Here, we update the ref with the updatedBlocks and add any new
     * blocks returned from the stream at the end
     */
    setBlocks(previousBlocks.current.concat(newBlocks));
    updateLoadingConfiguration({
      ...constants.defaultLoadingState,
      submission: false,
    });
    setNewAiMessageStatus(NewAiMessageStatus.Complete);
  };

  const handleOnStreamError = (): void => {
    if (onError) {
      onError({
        eventType: WiseOwlEventEnum.streamError,
        data: { chatId: chatId.current },
        error: Error("Document sync failed"),
      });
    }
    updateLoadingConfiguration({
      ...constants.defaultLoadingState,
      submission: false,
    });
    setNewAiMessageStatus(NewAiMessageStatus.None);
  };

  const { handleDocumentSync } =
    useTwilioSyncDocument<WiseOwlSyncDocumentContent>({
      onComplete: handleOnStreamComplete,
      onError: handleOnStreamError,
      onInitialize: handleOnInitializeStream,
      onStream: handleOnStream,
    });

  // consider moving to helpers
  const generateNewUserMessage = (
    input: string,
    createChatOnRetry: boolean
  ): MessageBlock => {
    const newUserMessage: MessageBlock = createNewUserMessageBlock(
      input,
      createChatOnRetry
    );

    setBlocks((currentBlocks) => {
      /**
       * Deep-copy blocks up to this point. Streaming will write blocks on-top of this
       * state.
       */
      const newBlocks = cloneDeep(currentBlocks).concat(newUserMessage);

      /**
       * previousBlocks is officially "preStreamingBlocks". We 'freeze' our existing block state,
       * and then we're safe to write all incoming block(s) from a message's SyncDoc.
       *
       * see handleOnStream to see how we're doing this.
       */
      previousBlocks.current = newBlocks;
      return newBlocks;
    });
    return newUserMessage;
  };

  const handleStartNewChat = async (
    data: SendMessageFormValues
  ): Promise<void> => {
    try {
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: true,
      });

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.submitNewChat,
          data: {},
        });
      }

      const createChatOnRetry = true;
      const { input } = data;
      const newUserMessage = generateNewUserMessage(input, createChatOnRetry);

      const { success, data: response } = await createChatMutation(input);
      const { accessToken, syncDocumentSid, chatId: newChatId } = response;
      const {
        values: { retryId },
      } = newUserMessage;

      if (!success) {
        setFailedUserMessageId(retryId);
        setChatSubmissionDisabled(true);
        throw new Error(wiseOwlErrors.chatCreation);
      }

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.startNewChat,
          data: {
            chatId: newChatId,
            syncDocumentSid,
          },
        });
      }

      setChatSubmissionDisabled(false);
      chatId.current = newChatId;
      handleDocumentSync({
        accessToken,
        syncDocumentSid,
      });
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.startNewChat,
          error: err as Error,
          data: {},
        });
      }

      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: false,
      });
    }
  };

  const cleanupBlocksForRegeneration = (
    blocks: WiseOwlBlock[]
  ): WiseOwlBlock[] => {
    return blocks.filter(
      (block) =>
        (block as MessageBlock).values.completionStatus !==
        MessageCompletionStatus.Error
    );
  };

  const handleSendChatMessage = async (
    data: SendMessageFormValues
  ): Promise<void> => {
    try {
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: true,
      });
      setBlocks((currentBlocks) => cleanupBlocksForRegeneration(currentBlocks));
      const createChatOnRetry = false;
      const newUserMessage = generateNewUserMessage(
        data.input,
        createChatOnRetry
      );
      const {
        values: { retryId },
      } = newUserMessage;

      if (data.chatId) {
        const { success, data: response } = await sendMessageMutation({
          chatId: data.chatId,
          input: data.input,
        });

        if (!success) {
          setFailedUserMessageId(retryId);
          throw new Error(wiseOwlErrors.messageCreation);
        }

        const { accessToken, syncDocumentSid } = response;

        if (onEvent) {
          onEvent({
            eventType: WiseOwlEventEnum.sendChatMessage,
            data: {
              chatId: data.chatId,
              syncDocumentSid,
            },
          });
        }

        handleDocumentSync({
          accessToken,
          syncDocumentSid,
        });
      }
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.sendChatMessage,
          error: err as Error,
          data: {
            chatId: data.chatId,
          },
        });
      }
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: false,
      });
    }
  };

  const handleSubmitFeedback = async (
    data: CustomerFeedbackFormValues
  ): Promise<void> => {
    try {
      const { messageId, feedback, positive } = data;

      if (chatId.current) {
        const { success } = await createFeedbackMutation({
          chatId: chatId.current,
          messageId,
          feedback,
          positive,
        });

        if (!success) {
          throw new Error(wiseOwlErrors.feedbackCreation);
        }

        if (onEvent) {
          onEvent({
            eventType: WiseOwlEventEnum.submitFeedback,
            data: {
              ...data,
              positive: positive ? "positive" : "negative",
              chatId: chatId.current,
            },
          });
        }
      }
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.submitFeedback,
          error: err as Error,
          data: { chatId: chatId.current },
        });
      }
      throw err;
    }
  };

  const handleRedirectAction = (redirectUrl: string): void => {
    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.redirect,
        data: { chatId: chatId.current, redirectUrl },
      });
    }
  };

  const handleMessageRetry = (retryId: string): void => {
    const { failedMessageIndex, failedMessage } = findLastFailedUserMessage(
      blocks,
      retryId
    );

    setBlocks((currentBlocks) =>
      spliceBlocks(currentBlocks, failedMessageIndex)
    );

    if (failedMessage.values.createChatOnRetry) {
      handleStartNewChat({ input: failedMessage.values.message });

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.startNewChatRetry,
          data: { retryId },
        });
      }
    } else {
      handleSendChatMessage({
        input: failedMessage.values.message,
        chatId: chatId.current,
      });

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.sendChatMessageRetry,
          data: { retryId },
        });
      }
    }
  };

  const removeBlockAtIndex = (
    blocks: WiseOwlBlock[],
    blockToRemoveIndex: number
  ): WiseOwlBlock[] => {
    blocks.splice(blockToRemoveIndex, 1);
    return blocks;
  };

  const handleResponseRegeneration = (regenerationId: string): void => {
    const [failedUserPromptMessage, failedUserPromptIndex] =
      findUserPromptMessageToRegenerateAssistantMessage(blocks, regenerationId);
    setBlocks((currentBlocks) =>
      removeBlockAtIndex(currentBlocks, failedUserPromptIndex)
    );
    handleSendChatMessage({
      input: failedUserPromptMessage.values.message,
      chatId: chatId.current,
    });
    updateLoadingConfiguration({
      ...constants.defaultLoadingState,
      submission: false,
    });
  };

  const handleLoginRedirect = () => {
    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.loginRedirect,
        data: {
          chatId: chatId.current,
        },
      });
    }

    if (onTrackEvent) {
      onTrackEvent({
        eventName: trackingDetails.wiseOwl.feedbackLogin(displayName),
        additionalData: {
          chatId: chatId.current,
          ingress,
        },
      });
    }
  };

  const handleTrackEvent = (
    eventName: string,
    additionalData: Record<string, unknown>
  ): void => {
    if (onTrackEvent) {
      onTrackEvent({
        eventName,
        additionalData,
      });
    }
  };

  const handleGetChatSummary = async (): Promise<
    WiseOwlGetChatSummaryByIdResponseData | undefined
  > => {
    try {
      if (chatId.current) {
        const { success, data: response } = await getChatSummaryMutation(
          chatId.current
        );

        const { subject, description, wasTranslated } = response;

        if (!success) {
          throw new Error(wiseOwlErrors.getChatSummary);
        }

        if (onEvent) {
          onEvent({
            eventType: WiseOwlEventEnum.getChatSummary,
            data: {
              chatId: chatId.current,
            },
          });
        }

        return { subject, description, wasTranslated };
      }
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.getChatSummary,
          error: err as Error,
          data: {
            chatId: chatId.current,
          },
        });
      }
    }
  };

  /**
   * Handle live chat transfer from an existing WO chat
   *
   * Note: No try/catch to propagate errors to handleTransferToLiveChat
   */
  const handleLiveChatTransferFromExistingChat = async ({
    chatId,
    productCategory,
    question,
    ingressPreEngagementData,
    subProduct,
  }: HandleLiveChatTransferArgs): Promise<WiseOwlTransferToLiveChatResponseData> => {
    const preEngagementData = buildLiveChatPreEngagementData({
      ingressPreEngagementData,
      productCategory,
      question,
      ingress,
      subProduct,
    });

    const customerFriendlyName =
      ingressPreEngagementData.friendlyName || "Chat User";

    const { success, data: response } = await transferToLiveChatMutation({
      chatId,
      chatFriendlyName: `Live Chat from ${customerFriendlyName}`,
      customerFriendlyName,
      preEngagementData,
    });

    if (!success) {
      throw new Error(wiseOwlErrors.transferToLiveChat);
    }

    return response;
  };

  /**
   * Handle direct live chat creation. Live chat is accessible directly on WO home page,
   * this pathway will 1) create a WO chat and then 2) transfer to live chat
   *
   * This logic may change as we reconsider standalone live chat.
   *
   * Note: No try/catch to propagate errors to handleTransferToLiveChat
   */
  const handleDirectLiveChatCreation = async ({
    productCategory,
    question,
    ingressPreEngagementData,
    subProduct,
  }: HandleDirectChatCreationArgs): Promise<WiseOwlTransferToLiveChatResponseData> => {
    const input = `${question} (Non-HCA transfer Chat)`;
    const { success: createNewChatSuccess, data: createNewChatResponse } =
      await createChatMutation(input);
    const { chatId: newChatId } = createNewChatResponse;
    chatId.current = newChatId;

    if (!createNewChatSuccess) {
      throw new Error(wiseOwlErrors.directLiveChatNewChatCreation);
    }

    const preEngagementData = buildLiveChatPreEngagementData({
      ingressPreEngagementData,
      productCategory,
      question,
      ingress,
      subProduct,
      additionalMetadata: {
        tags: [ZendeskTags.directLiveChatCreation],
        hcaCsatProps: {
          chatId: chatId.current,
          chatTimestamp: new Date().toISOString(),
          ingress: ingress,
          firstName: user.firstName,
          lastName: user.lastName,
        },
      },
    });

    const { success, data: response } = await transferToLiveChatMutation({
      chatId: chatId.current,
      chatFriendlyName: `Live Chat from ${ingressPreEngagementData.friendlyName}`,
      customerFriendlyName:
        ingressPreEngagementData.friendlyName || "Chat User",
      preEngagementData,
    });

    if (!success) {
      throw new Error(wiseOwlErrors.directLiveChatCreation);
    }

    return response;
  };

  const composeProductCategoryData = (productCategory: string) => {
    if (ingress === WiseOwlIngress.sendGrid) {
      return {
        productCategory: "email",
        subProduct: productCategory,
        ingressPreEngagementData,
      };
    }

    return {
      productCategory,
    };
  };

  const handleTransferToLiveChat = async (
    productCategory: string,
    question: string
  ): Promise<void> => {
    try {
      // TODO: How can we enforce this with TS versus manually throwing an error?
      // Gather Pre-Engagement data
      if (!ingressPreEngagementData) {
        throw new Error(wiseOwlErrors.missingPreEngagementData);
      }

      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: true,
        fetchChatById: true,
      });

      const baseArgs = {
        ...composeProductCategoryData(productCategory),
        question,
        ingressPreEngagementData,
      };

      const response = chatId.current
        ? await handleLiveChatTransferFromExistingChat({
            ...baseArgs,
            chatId: chatId.current,
          })
        : await handleDirectLiveChatCreation(baseArgs);

      const { blocks: newBlocks } = response;

      const liveChatBlock = newBlocks.find(
        (block) => block.type === WiseOwlBlockType.LiveChat
      );

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.startNewChat,
          data: {
            chatId: chatId.current,
            isLiveChatActive: true,
            liveChatId: liveChatBlock?.values.liveChatId,
          },
        });
      }

      setBlocks(newBlocks);
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.liveChatTransfer,
          error: err as Error,
          data: {
            chatId: chatId.current,
          },
        });
      }
    }

    updateLoadingConfiguration({
      ...constants.defaultLoadingState,
      fetchChatById: false,
      submission: false,
    });
  };

  const handleToggleLiveChatPreEngagement = (): void => {
    setIsLiveChatPreEngagementOpen(!isLiveChatPreEngagementOpen);
  };

  const handleUpdateLiveChatPreEngagementValues = (
    liveChatPreEngagementValues: LiveChatPreEngagementValues
  ): void => {
    setLiveChatPreEngagementValues(liveChatPreEngagementValues);
  };

  const handleContextInit = (context: WiseOwlContextProps): void => {
    if (onContextInit) {
      onContextInit(context);
    }
  };

  // Init WO Context
  const context = {
    applicationProps: props,
    ingress,
    chatId: chatId.current,
    loadingConfiguration,
    isLoggedIn: !stargateUngatedUserId,
    isLiveChatPreEngagementOpen,
    liveChatConfiguration: {
      preEngagementValues:
        liveChatConfiguration?.preEngagementValues ||
        liveChatPreEngagementValues,
      waitingActions: liveChatConfiguration?.waitingActions,
      endedActions: liveChatConfiguration?.endedActions,
    },
    author: `${user.firstName} ${user.lastName}`,
    handlers: {
      handleSendChatMessage,
      handleStartNewChat,
      handleSubmitFeedback,
      handleRedirectAction,
      handleLoginRedirect,
      handleTrackEvent,
      handleMessageRetry,
      handleResponseRegeneration,
      handleGetChatSummary,
      handleTransferToLiveChat,
      handleUpdateLiveChatPreEngagementValues,
      handleToggleLiveChatPreEngagement,
      handleContextInit,
      handleChatButtonToolClick,
      handleForeslashCommand: handleDisplayPrompt,
    },
    user,
  };

  const isLiveChatActive = isLiveChat(blocks);
  const surveyVariant = isLiveChatActive
    ? SurveyVariantEnum.AGENT
    : SurveyVariantEnum.HELP_CENTER_ASSISTANT;

  const isChatActive = !!chatId && hasMessages(blocks);
  const liveChatValues = getLiveChatValuesFromBlocks(blocks);
  // Init Survey Context
  const surveyContext = {
    chatId: chatId.current,
    displayName,
    isChatActive,
    ingress,
    onEvent,
    onError,
    onTrackEvent,
    surveyId,
    surveyVariant,
    user,
    liveChatId: liveChatValues?.liveChatId,
    ticketId: liveChatValues?.ticketId,
    token: props.token,
  };

  return (
    <Theme.Provider
      theme="twilio"
      style={{
        flex: "1",
        overflowY:
          ingress === WiseOwlIngress.wiseOwlApp ||
          ingress === WiseOwlIngress.sendGrid
            ? "auto"
            : "scroll",
        // Removes double scrollbar when on mobile
        scrollbarWidth:
          window.innerWidth < 600 || ingress === WiseOwlIngress.console
            ? "none"
            : undefined,
      }}
    >
      <WiseOwlProvider context={context}>
        <SurveyProvider context={surveyContext}>
          <WiseOwlStyleContainer
            element={WiseOwlElementIds.WISE_OWL_CONTAINER}
            customStyles={customStyles}
          >
            <WiseOwlChat
              loadingConfiguration={loadingConfiguration}
              chatId={chatId.current}
              chatHistory={[]}
              blocks={blocks}
              newAiMessageStatus={newAiMessageStatus}
              onStartNewChat={handleStartNewChat}
              onChatButtonToolClick={handleChatButtonToolClick}
              onDisplayPromptLibrary={handleDisplayPrompt}
              onSendChatMessage={handleSendChatMessage}
              onSubmitFeedback={handleSubmitFeedback}
              handleRetry={handleMessageRetry}
              handleResponseRegeneration={handleResponseRegeneration}
              disabled={disableChatMessageSubmission}
            />
            <Box
              as="div"
              className={
                isChatActive
                  ? styles.hideLegalDisclaimer
                  : styles.showLegalDisclaimer
              }
            >
              <LegalDisclaimer />
            </Box>
          </WiseOwlStyleContainer>
        </SurveyProvider>
      </WiseOwlProvider>
    </Theme.Provider>
  );
};

export default WiseOwl;
